import * as dateFns from "date-fns";
import { TLanguages } from "../languages/languageData";
import { Duration } from "../interfaces/duration.interface";
import { GenericVehicle, VehicleAlertSummary } from "../interfaces/socket-dto.interface";
import { TRACKER_NO_SIGNAL } from "../constants/system.constants";

/** Format hour meter in hours and minutes
 *
 * The input MUST be in minutes (e.g. the tracker's `H_METER`). For values already in decimal hours
 * (the CAN counters) use {@link formatDecimalHours} instead.
 *
 * @param hourMeter Hour meter value in minutes
 */
export function formatHourMeter(
  hourMeter?: number
): string {

  if (!hourMeter && hourMeter !== 0) return "";

  const total = (hourMeter / 60);

  const hour = Math.floor(total);
  const decimalPart = total - hour;

  if (decimalPart === 0) return `${hour}h`;

  const minutes = Math.floor(decimalPart * 60);

  if (hour === 0) return `${minutes}m`;

  return `${hour}h ${minutes}m`;
}

/** Format a value already expressed in DECIMAL HOURS as hours and minutes.
 *
 * Use this — never {@link formatHourMeter} — for CAN counters such as `CAN_TOTAL_ENGINE_HOURS` and
 * `CAN_TOTAL_DRIVING_TIME`, which the trackers report in hours. Feeding hours to `formatHourMeter`
 * (which expects minutes) divides the reading by 60: 3559.52 h renders as "59h 19m".
 *
 * @param decimalHours Value in hours (e.g. 3559.52)
 */
export function formatDecimalHours(
  decimalHours?: number
): string {

  if (!decimalHours && decimalHours !== 0) return "";

  return formatHourMeter(decimalHours * 60);
}

/** Format odometer in meter to kilometer
 * @param odometer Odometer value in meter
 * @param appendKM
 * @param decimalSeparator
 * @param decimalPlaces
 */
export function formatOdometer(
  odometer: number,
  appendKM = true,
  decimalSeparator: "." | "," = ",",
  decimalPlaces = 1
): string {
  let km = (odometer / 1000).toFixed(decimalPlaces);

  if (decimalSeparator === ",") km = km.replace(".", ",");

  if (appendKM) return `${km} KM`;

  return km;
}

/**
 * Format fuel level value with unit
 * @param value Fuel level value with prefix (P for percentage, L for liters), or the no-signal sentinel
 */
export function formatFuelLevel(value: string): string {
  if (!value || value === TRACKER_NO_SIGNAL) return "";
  if (value[0] === "P") return `${value.slice(1)} %`;
  if (value[0] === "L") return `${value.slice(1)} L`;

  return value;
}

/**
 * Format fuel consumption value with unit
 *
 * The prefix carries the unit — "H" = L/h, "M" = L/100km — and an UNKNOWN prefix is rendered as a
 * bare number, WITHOUT a unit: the Jmak J-R12 emits "L" in production (its parser forwards whatever
 * unit byte the payload carries, see Backend-Socket/src/sockets/trackers/Jmak/shared/jr12-parser.ts)
 * and that unit is NOT confirmed by the manufacturer — measuring the reported value against the
 * CAN_TOTAL_FUEL_USED delta was inconclusive (ratio 0.16 over only 3 valid samples). Guessing "L/h"
 * would be a fabricated unit; pending confirmation, show the number alone.
 *
 * @param value Fuel consumption value with prefix (H for L/h, M for L/100km), or the no-signal sentinel
 */
export function formatFuelConsumption(value: string | number): string {
  if (!value || value === TRACKER_NO_SIGNAL) return "";
  if (typeof value === "string") {
    if (value[0] === "H") return `${value.slice(1)} L/h`;
    if (value[0] === "M") return `${value.slice(1)} L/100km`;
    if (/^[A-Za-z]/.test(value)) return value.slice(1);
  }

  return String(value);
}

/**
 * Format date and hour
 * @param date Date or Hour or Interval
 * @param type Type of first parameter
 * @param language Language to format date
 */
export function formatDateIfHave(
  date: Duration | Date | string | undefined,
  type: "fullDateWithoutYear" | "fullDateWithoutSeconds" | "fullDateFormat" | "durationDescriptiveTime" | "durationIntervalTime",
  language?: TLanguages
): string {

  // Define formats according to language
  const fullDateWithoutYearFormat = language === "en-US" ? "MM/dd HH:mm:ss" : "dd/MM HH:mm:ss";
  const fullDateWithoutSecondsFormat = language === "en-US" ? "MM/dd/yyyy HH:mm" : "dd/MM/yyyy HH:mm";
  const fullDateFormat = language === "en-US" ? "MM/dd/yyyy HH:mm:ss" : "dd/MM/yyyy HH:mm:ss";

  let intervalDate;

  if (!date) return "";
  if (typeof date === "object") intervalDate = date as Duration;
  else intervalDate = date as Date | string;

  switch (type) {
  case "fullDateWithoutYear":
    return dateFns.format(new Date(intervalDate), fullDateWithoutYearFormat);
  case "fullDateWithoutSeconds":
    return dateFns.format(new Date(intervalDate), fullDateWithoutSecondsFormat);
  case "fullDateFormat":
    return dateFns.format(new Date(intervalDate), fullDateFormat);
  case "durationDescriptiveTime": {
    const duration = date as Duration;
    let formattedTime = "";
    const hours = duration.hours || 0;
    const minutes = duration.minutes || 0;
    const seconds = duration.seconds || 0;

    if (hours > 0) formattedTime += `${hours}h `;
    if (minutes > 0) formattedTime += `${minutes}m `;
    if (hours === 0 && minutes === 0) formattedTime += `${seconds}s`;

    return formattedTime;
  }
  case "durationIntervalTime": {

    const initialDate = { hours: "00", minutes: "00", seconds: "00" };

    Object.keys(intervalDate).forEach((key) => initialDate[key] = intervalDate[key].toString().padStart(2, "0"));

    return `${initialDate.hours}:${initialDate.minutes}:${initialDate.seconds}`;
  }
  default:
    return "";
  }
}

/**
 * Calc the duration (range) between two dates
 * @param dateA
 * @param dateB
 */
export function calcDataRange(dateA: Date, dateB: Date) {

  if (!dateA || !dateB) return "";

  const difSeconds = dateFns.differenceInSeconds(new Date(dateB), new Date(dateA));
  const secNum = parseInt(difSeconds.toString(), 10);
  const hours = Math.floor(secNum / 3600);
  const minutes = Math.floor(secNum / 60) % 60;
  const seconds = secNum % 60;

  return { hours, minutes, seconds } as Duration;
}

/**
 * Sum duration array
 * @param durations Array of durations
 */
export function sumDurations(durations: Duration[]): Duration {

  if (!durations || durations.length === 0) return { hours: 0, minutes: 0, seconds: 0 };

  return durations.reduce((acc, cur) => {

    acc.hours = acc.hours ?? 0;
    acc.minutes = acc.minutes ?? 0;
    acc.seconds = acc.seconds ?? 0;

    acc.hours += cur.hours ?? 0;
    acc.minutes += cur.minutes ?? 0;
    acc.seconds += cur.seconds ?? 0;

    if (acc.seconds >= 60) {
      acc.minutes += 1;
      acc.seconds -= 60;
    }

    if (acc.minutes >= 60) {
      acc.hours += 1;
      acc.minutes -= 60;
    }

    return acc;
  }, { hours: 0, minutes: 0, seconds: 0 });
}

/**
 * Pauses execution for the specified number of milliseconds.
 *
 * @param ms - Milliseconds to wait
 * @returns A Promise that resolves after the delay
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Convert string to array if it is not
 * @param data
 */
export function convertToArray(data: string | string[]): string[] {
  if (typeof data === "string") return [data];

  return data;
}

/**
 * Verify if GenericVehicle is a VehicleAlertSummary
 * @param vehicleData Vehicle to verify
 */
export function isVehicleAlertSummary(vehicleData: GenericVehicle): vehicleData is VehicleAlertSummary {
  return !Object.prototype.hasOwnProperty.call(vehicleData, "alerts");
}
